import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Button, Group, InlineError, Stack, Text } from '@wapharma/design-system';
import { unwrapData } from '@wapharma/shared';
import {
  DEFAULT_LOW_STOCK_THRESHOLD,
  DEFAULT_NEAR_EXPIRY_WARN_DAYS,
  LOW_STOCK_THRESHOLD_KEY,
  NEAR_EXPIRY_WARN_DAYS_KEY,
} from '@wapharma/domain';
import { apiGet, apiPut } from '../api';
import { getDisplayError } from '../apiErrors';
import { getToken } from '../auth';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { NearExpiryDaysVisual, StockLowThresholdVisual } from './StockAlertsVisualFields';

type StockAlertGlobals = { lowStockThreshold: number; nearExpiryWarnDays: number };

export type StockAlertsSettingsFacet = 'all' | 'lowStock' | 'nearExpiry';

export type StockAlertsSettingsHandle = {
  /** Persist current facet values. Returns false on validation/API failure. */
  save: () => Promise<boolean>;
};

export const StockAlertsSettingsSection = forwardRef<
  StockAlertsSettingsHandle,
  {
    facet?: StockAlertsSettingsFacet;
    showTitle?: boolean;
    /** Hide Enregistrer — parent persists via ref.save() (setup Continuer). */
    hideSaveButton?: boolean;
    quietToast?: boolean;
  }
>(function StockAlertsSettingsSection(
  { facet = 'all', showTitle = true, hideSaveButton = false, quietToast = false },
  ref,
) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const canWrite = user?.roles.includes('SUPER_ADMIN') ?? false;
  const token = getToken() ?? undefined;
  const [low, setLow] = useState(DEFAULT_LOW_STOCK_THRESHOLD);
  const [near, setNear] = useState(DEFAULT_NEAR_EXPIRY_WARN_DAYS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showLow = facet === 'all' || facet === 'lowStock';
  const showNear = facet === 'all' || facet === 'nearExpiry';

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet<{ data: StockAlertGlobals }>(`/admin/settings/stock-alerts`, token);
      const data = unwrapData(res) as StockAlertGlobals;
      setLow(data.lowStockThreshold);
      setNear(data.nearExpiryWarnDays);
    } catch (e) {
      setError(getDisplayError(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const persist = useCallback(async (): Promise<boolean> => {
    if (!token || !canWrite) return true;
    if (showLow && (!Number.isFinite(low) || low < 0)) {
      setError('Seuil stock bas : entier ≥ 0.');
      return false;
    }
    if (showNear && (!Number.isFinite(near) || near < 1)) {
      setError('Alerte péremption : entier ≥ 1 jour.');
      return false;
    }
    setSaving(true);
    setError(null);
    try {
      if (showLow) {
        await apiPut(
          `/admin/settings/${encodeURIComponent(LOW_STOCK_THRESHOLD_KEY)}`,
          { value: String(Math.trunc(low)) },
          token,
        );
      }
      if (showNear) {
        await apiPut(
          `/admin/settings/${encodeURIComponent(NEAR_EXPIRY_WARN_DAYS_KEY)}`,
          { value: String(Math.trunc(near)) },
          token,
        );
      }
      if (!quietToast) {
        showToast(
          facet === 'lowStock'
            ? 'Seuil stock bas enregistré.'
            : facet === 'nearExpiry'
              ? 'Alerte péremption enregistrée.'
              : 'Alertes stock enregistrées.',
          { tone: 'success' },
        );
      }
      return true;
    } catch (e) {
      const msg = getDisplayError(e);
      setError(msg);
      showToast(msg, { tone: 'error' });
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    canWrite,
    facet,
    low,
    near,
    quietToast,
    showLow,
    showNear,
    showToast,
    token,
  ]);

  useImperativeHandle(ref, () => ({ save: persist }), [persist]);

  return (
    <Stack gap="md">
      {showTitle ? (
        <Text size="sm" fw={600}>
          {facet === 'lowStock'
            ? 'Stock bas'
            : facet === 'nearExpiry'
              ? 'Péremption'
              : 'Alertes stock'}
        </Text>
      ) : null}
      {loading ? (
        <Text size="sm" c="dimmed">
          Chargement…
        </Text>
      ) : (
        <Stack gap="lg">
          {showLow ? (
            <StockLowThresholdVisual value={low} onChange={setLow} disabled={!canWrite} />
          ) : null}
          {showNear ? (
            <NearExpiryDaysVisual value={near} onChange={setNear} disabled={!canWrite} />
          ) : null}
          {error ? <InlineError>{error}</InlineError> : null}
          {!hideSaveButton && canWrite ? (
            <Group justify="flex-end">
              <Button
                type="button"
                variant="primary"
                disabled={saving}
                onClick={() => void persist()}
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </Group>
          ) : null}
          {!hideSaveButton && !canWrite ? (
            <Text size="xs" c="dimmed">
              Lecture seule
            </Text>
          ) : null}
        </Stack>
      )}
    </Stack>
  );
});
